const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "session_expires_at";
const RENEW_INTERVAL_MS = 15 * 60 * 1000;
const RENEW_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

let sessionExpiresAt = null;
let renewTimer = null;
let visibilityHandler = null;
let unauthorizedHandler = null;

export class UnauthorizedError extends Error {
  constructor(message = "Unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

const parseSessionExpiry = (isoDate) => {
  if (!isoDate) return null;
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

const handleUnauthorized = () => {
  clearSessionExpiry();
  if (typeof unauthorizedHandler === "function") {
    unauthorizedHandler();
  }
};

export const setSessionExpiry = (isoDate) => {
  const parsed = parseSessionExpiry(isoDate);
  sessionExpiresAt = parsed;

  if (parsed) {
    localStorage.setItem(STORAGE_KEY, parsed.toISOString());
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

export const loadSessionExpiry = () => {
  if (sessionExpiresAt) return sessionExpiresAt;
  const storedValue = localStorage.getItem(STORAGE_KEY);
  const parsed = parseSessionExpiry(storedValue);
  sessionExpiresAt = parsed;
  return parsed;
};

export const clearSessionExpiry = () => {
  sessionExpiresAt = null;
  localStorage.removeItem(STORAGE_KEY);
};

export const isSessionExpiringSoon = () => {
  const currentExpiry = loadSessionExpiry();
  if (!currentExpiry) return false;
  return currentExpiry.getTime() - Date.now() <= RENEW_THRESHOLD_MS;
};

export const apiFetch = async (path, options = {}) => {
  const {
    skipUnauthorizedRedirect = false,
    headers,
    ...restOptions
  } = options;

  const response = await fetch(`${API}${path}`, {
    credentials: "include",
    ...restOptions,
    headers,
  });

  if (response.status === 401) {
    if (!skipUnauthorizedRedirect) {
      handleUnauthorized();
      throw new UnauthorizedError();
    }
  }

  return response;
};

export const refreshSession = async () => {
  const response = await apiFetch("/auth/refresh", {
    method: "POST",
  });

  if (response.ok) {
    const payload = await response.json();
    setSessionExpiry(payload.expires_at);
    return payload;
  }

  return null;
};

export const syncSessionStatus = async () => {
  const response = await apiFetch("/auth/session-status", {
    method: "GET",
  });

  if (response.ok) {
    const payload = await response.json();
    setSessionExpiry(payload.expires_at);
    return payload;
  }

  return null;
};

const renewIfNeeded = async () => {
  if (!isSessionExpiringSoon()) return;
  await refreshSession();
};

export const startSessionAutoRenewal = () => {
  stopSessionAutoRenewal();

  renewTimer = window.setInterval(() => {
    renewIfNeeded().catch(() => {});
  }, RENEW_INTERVAL_MS);

  visibilityHandler = () => {
    if (document.visibilityState === "visible") {
      renewIfNeeded().catch(() => {});
    }
  };

  document.addEventListener("visibilitychange", visibilityHandler);
  renewIfNeeded().catch(() => {});
};

export const stopSessionAutoRenewal = () => {
  if (renewTimer) {
    window.clearInterval(renewTimer);
    renewTimer = null;
  }

  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
};
